import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Car,
  BarChart3,
  Settings,
  KeyRound,
} from 'lucide-react';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  adminOnly?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  { to: '/', label: t('nav.dashboard'), icon: LayoutDashboard },
  { to: '/keys', label: t('nav.inventory'), icon: Package },
  { to: '/sales', label: t('nav.sales'), icon: ShoppingBag },
  { to: '/compat', label: t('nav.compat'), icon: Car },
  { to: '/reports', label: t('nav.reports'), icon: BarChart3 },
  { to: '/settings', label: t('nav.settings'), icon: Settings, adminOnly: true },
];

interface SidebarProps {
  collapsed?: boolean;
  onNavigate?: () => void;
  isAdmin?: boolean;
}

export function Sidebar({ collapsed, onNavigate, isAdmin }: SidebarProps) {
  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r bg-card transition-[width] duration-200',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <div
        className={cn(
          'flex h-16 items-center gap-2 border-b px-4',
          collapsed && 'justify-center px-0',
        )}
      >
        <div className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-primary/10 text-primary">
          <KeyRound className="h-5 w-5" />
        </div>
        {!collapsed ? (
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{t('app.name')}</div>
            <div className="truncate text-[11px] text-muted-foreground">{t('app.tagline')}</div>
          </div>
        ) : null}
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-2">
        {NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin).map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  collapsed && 'justify-center px-0',
                )
              }
              title={collapsed ? item.label : undefined}
            >
              <Icon className="h-4 w-4 flex-none" />
              {!collapsed ? <span className="truncate">{item.label}</span> : null}
            </NavLink>
          );
        })}
      </nav>

      {!collapsed ? (
        <div className="border-t p-3 text-[11px] text-muted-foreground">
          <span>Smart Key Sofia &copy; {new Date().getFullYear()}</span>
        </div>
      ) : null}
    </aside>
  );
}
